import {
    Checkbox,
    CollapsibleContent,
    Divider,
    Input,
    InputError,
    LabeledContainer,
    Select,
} from '@schibsted-svp/react-ui';
import { PodcastCategoriesSelect } from 'components/categories/Form/PodcastCategories/PodcastCategoriesSelect';
import { PercentInput } from 'components/core/hookForm/PercentInput';
import { findSingleOption, getValueFromOption } from 'components/ui/Form/Select';
import QuestionMark from 'components/ui/QuestionMark';
import { isPodmeIntegrationEnabled } from 'lib/flags';
import { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { PodcastAdPlacement } from './PodcastAdPlacement';
import { PodmeIntegration } from './PodmeIntegration';
import type { CategoryData } from './types';

import css from './PodcastProperties.module.scss';

const podcastTypesOptions = [
    {
        value: 'episodic',
        label: 'episodic',
    },
    {
        value: 'serial',
        label: 'serial',
    },
];

const slotTypes = ['preroll', 'midroll', 'postroll'] as const;

type PodcastPropertiesProps = {
    provider: string;
    categoryId: number;
    isCreationMode: boolean;
};

export function PodcastProperties({ provider, categoryId, isCreationMode }: PodcastPropertiesProps) {
    const { register, formState, control, setValue, trigger } = useFormContext<CategoryData>();
    const { errors } = formState;

    const defaultAutomaticAds = formState.defaultValues?.additional?.metadata?.podcast_automaticAds?.value;
    const [isAutomaticAdsOn, setIsAutomaticAdsOn] = useState(Boolean(defaultAutomaticAds));

    useEffect(() => setIsAutomaticAdsOn(Boolean(defaultAutomaticAds)), [formState.defaultValues, defaultAutomaticAds]);

    const normalizeExplicit = (event: ChangeEvent<HTMLInputElement>) => {
        setValue('additional.metadata.podcast_explicit.value', event.target.checked ? 'yes' : null);
    };

    const onAutomaticAdsChange = (event) => {
        const isChecked = event.target.checked;
        setIsAutomaticAdsOn(Boolean(isChecked));
        if (!isChecked) {
            setValue('additional.metadata.podcast_automaticAds.value', null);
            setValue('additional.metadata.podcast_customPrerollPosition.value', null);
            setValue('additional.metadata.podcast_customPostrollPosition.value', null);
        }
    };

    return (
        <CollapsibleContent label="podcast properties">
            <section className={css.content}>
                <PodcastCategoriesSelect control={control} />
                <LabeledContainer label="AUTHOR" strict>
                    <Input
                        {...register('additional.metadata.podcast_author.value')}
                        size="small"
                        error={errors?.additional?.metadata?.podcast_author?.value?.message}
                    />
                </LabeledContainer>
                <LabeledContainer label="LINK" strict>
                    <Input
                        {...register('additional.metadata.podcast_link.value')}
                        size="small"
                        error={errors?.additional?.metadata?.podcast_link?.value?.message}
                    />
                </LabeledContainer>
                <LabeledContainer label="EMAIL" strict>
                    <Input
                        {...register('additional.metadata.podcast_ownerEmail.value')}
                        size="small"
                        error={errors?.additional?.metadata?.podcast_ownerEmail?.value?.message}
                    />
                </LabeledContainer>
                <LabeledContainer label="ACAST SHOW ID" strict>
                    <Input {...register('additional.metadata.podcast_acast_showId.value')} size="small" disabled />
                </LabeledContainer>
                <LabeledContainer label="LANGUAGE" strict>
                    <Input
                        {...register('additional.metadata.podcast_language.value')}
                        size="small"
                        error={errors?.additional?.metadata?.podcast_language?.value?.message}
                    />
                </LabeledContainer>
                <LabeledContainer label="OWNER NAME" strict>
                    <Input {...register('additional.metadata.podcast_ownerName.value')} size="small" />
                </LabeledContainer>
                <LabeledContainer label="COPYRIGHT" strict>
                    <Input
                        {...register('additional.metadata.podcast_copyright.value')}
                        placeholder={`Copyright: ${provider.toUpperCase()}`}
                        size="small"
                    />
                </LabeledContainer>
                <LabeledContainer label="PREPEND URL" strict>
                    <Input
                        {...register('additional.metadata.podcast_acast_prependUrl.value')}
                        size="small"
                        error={errors?.additional?.metadata?.podcast_acast_prependUrl?.value?.message}
                    />
                </LabeledContainer>
                <LabeledContainer label="TYPE" strict className={css.type}>
                    <Controller
                        name="additional.metadata.podcast_type.value"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                name="additional.metadata.podcast_type.value"
                                size="small"
                                options={podcastTypesOptions}
                                value={findSingleOption(podcastTypesOptions)(field.value)}
                                onChange={(values) => field.onChange(getValueFromOption(values))}
                                splitButton={false}
                            />
                        )}
                    />
                </LabeledContainer>
                <Checkbox
                    {...register('additional.metadata.podcast_explicit.value')}
                    label="Explicit"
                    containerClassName={css.explicit}
                    onChange={normalizeExplicit}
                />

                <LabeledContainer label="AD PLACEMENT IN EPISODES" className={css.adPlacement}>
                    <Divider className={css.adPlacementDivider} />
                    <p className={css.adsInfo}>Set the number of sponsorship and airtime ads per break.</p>
                    <div className={css.rolls}>
                        {slotTypes.map((slot, index) => (
                            <span key={slot}>
                                <PodcastAdPlacement adType={slot} register={register} errors={errors} />
                                {slotTypes.length - 1 !== index && <Divider margin="0 0" />}
                            </span>
                        ))}
                    </div>
                    <p className={css.midrollInfo}>
                        There can be several midroll breaks set in an episode.
                        <p>All midroll breaks have the same number of sponsorships and airtimes.</p>
                    </p>
                    <Divider margin="5px 0 10px" />

                    <span className={css.automaticAds}>
                        <CollapsibleContent
                            customHeader={
                                <Checkbox
                                    label="Set ad breaks automatically"
                                    checked={isAutomaticAdsOn}
                                    onChange={onAutomaticAdsChange}
                                />
                            }
                            expanded={isAutomaticAdsOn}
                            hideArrowIcon
                            headerClassName={css.automaticAdsHeader}
                        >
                            <div className={css.automaticAdsForm}>
                                <span className={css.label}>Number of automatic breaks:</span>
                                <Input
                                    {...register('additional.metadata.podcast_automaticAds.value', {
                                        onChange: () =>
                                            trigger([
                                                'additional.metadata.podcast_customPrerollPosition.value',
                                                'additional.metadata.podcast_customPostrollPosition.value',
                                            ]),
                                    })}
                                    size="small"
                                    className={css.numberInput}
                                    disabled={!isAutomaticAdsOn}
                                />
                                <InputError className={css.error} hideWhenEmpty>
                                    {errors?.additional?.metadata?.podcast_automaticAds?.value?.message}
                                </InputError>
                                <span className={css.label}>
                                    Custom preroll position:{' '}
                                    <QuestionMark content="Choose when the preroll ad should play, based on a percentage of the total podcast duration. For example, setting this to 0% places the ad at the beginning." />
                                </span>
                                <PercentInput
                                    name="additional.metadata.podcast_customPrerollPosition.value"
                                    control={control}
                                    className={css.percentInput}
                                    containerClassName={css.percentInputWrapper}
                                    disabled={!isAutomaticAdsOn}
                                />
                                <InputError className={css.error} hideWhenEmpty>
                                    {errors?.additional?.metadata?.podcast_customPrerollPosition?.value?.message}
                                </InputError>
                                <span className={css.label}>
                                    Custom postroll position:{' '}
                                    <QuestionMark content="Choose when the postroll ad should play, based on a percentage of the total podcast duration. For example, setting this to 100% places the ad at the end." />
                                </span>
                                <PercentInput
                                    name="additional.metadata.podcast_customPostrollPosition.value"
                                    control={control}
                                    className={css.percentInput}
                                    containerClassName={css.percentInputWrapper}
                                    disabled={!isAutomaticAdsOn}
                                />
                                <InputError className={css.error} hideWhenEmpty>
                                    {errors?.additional?.metadata?.podcast_customPostrollPosition?.value?.message}
                                </InputError>
                            </div>
                        </CollapsibleContent>
                    </span>
                </LabeledContainer>

                {!isCreationMode && isPodmeIntegrationEnabled && (
                    <PodmeIntegration
                        provider={provider}
                        categoryId={categoryId}
                        register={register}
                        control={control}
                    />
                )}
            </section>
        </CollapsibleContent>
    );
}
